<script>
export default {
  name: "ModernServices",
  data() {
    return {
      activeService: 0,
      autoSlideInterval: null,
      slideDelay: 2500, // 2.5 seconds between slides
      isPaused: false,
      services: [
        {
          imageSrc: "ethical.jpg",
          icon: "fas fa-hand-holding-heart",
          title: "Ethical & Transformative Leadership",
          description:
            "Preparing young people with ethical and transformative leadership skills to transform their families and communities.",
          features: [
            "Ethics & Integrity",
            "Critical Thinking",
            "Decision Making",
            "Growth Mindset",
          ],
          link: "/services",
        },
        {
          imageSrc: "workforce.jpg",
          icon: "fas fa-briefcase",
          title: "Workforce Development",
          description:
            "Equipping young people with practical skills and attitudes for workplace success through hands-on training.",
          features: [
            "Soft Skills Training",
            "Work Readiness",
            "Interview Skills",
            "Workplace Ethics",
          ],
          link: "/services",
        },
        {
          imageSrc: "leader1.jpg",
          icon: "fas fa-users",
          title: "Mentorship & Coaching",
          description:
            "Access to mentors and coaches across different professions who provide support and guidance to young people.",
          features: [
            "One-on-One Mentoring",
            "Career Guidance",
            "Professional Networking",
            "Skills Development",
          ],
          link: "/services",
        },
        {
          imageSrc: "enterprenuer1.jpg",
          icon: "fas fa-lightbulb",
          title: "Entrepreneurship & Enterprise",
          description:
            "Enabling young people to develop innovative solutions to unique problems in their communities.",
          features: [
            "Business Development",
            "Innovation Support",
            "Market Connections",
            "Problem Solving",
          ],
          link: "/services",
        },
        {
          imageSrc: "schoolimprovement.jpg",
          icon: "fas fa-school",
          title: "School Improvements Program",
          description:
            "Providing support to basic schools in the form of infrastructure and resources for a thriving learning environment.",
          features: [
            "Infrastructure Support",
            "Learning Resources",
            "Capacity Building",
            "Teacher Training",
          ],
          link: "/services",
        },
      ],
    };
  },
  mounted() {
    this.startAutoSlide();
  },
  beforeUnmount() {
    this.stopAutoSlide();
  },
  methods: {
    setActiveService(index) {
      this.activeService = index;
      // Reset the timer when manually clicking
      this.resetAutoSlide();
    },
    startAutoSlide() {
      this.autoSlideInterval = setInterval(() => {
        if (!this.isPaused) {
          this.nextSlide();
        }
      }, this.slideDelay);
    },
    stopAutoSlide() {
      if (this.autoSlideInterval) {
        clearInterval(this.autoSlideInterval);
        this.autoSlideInterval = null;
      }
    },
    resetAutoSlide() {
      this.stopAutoSlide();
      this.startAutoSlide();
    },
    nextSlide() {
      this.activeService = (this.activeService + 1) % this.services.length;
    },
    pauseSlide() {
      this.isPaused = true;
    },
    resumeSlide() {
      this.isPaused = false;
    },
  },
};
</script>

<template>
  <section class="relative bg-gray-50 py-20 lg:py-32 overflow-hidden">
    <!-- Background Decorations -->
    <div class="absolute inset-0 pointer-events-none">
      <div
        class="absolute top-0 left-0 w-1/3 h-full bg-logo-color/5"
        style="clip-path: polygon(0 0, 100% 0, 80% 100%, 0% 100%)"
      ></div>
      <div
        class="absolute -top-40 -right-40 w-80 h-80 bg-logo-color/10 rounded-full blur-3xl"
      ></div>
      <div
        class="absolute -bottom-40 -left-40 w-96 h-96 bg-logo-color/5 rounded-full blur-3xl"
      ></div>
    </div>

    <div class="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <!-- Header -->
      <div class="text-center mb-16" data-aos="fade-up" data-aos-duration="800">
        <div
          class="inline-flex items-center gap-2 px-4 py-2 bg-logo-color/10 rounded-full mb-6"
        >
          <i class="fas fa-star text-logo-color text-sm"></i>
          <span
            class="text-sm font-semibold text-logo-color uppercase tracking-wider"
          >
            Our Programs
          </span>
        </div>
        <h2
          class="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6"
        >
          What We <span class="text-logo-color">Do</span>
        </h2>
        <p class="text-lg text-gray-600 max-w-2xl mx-auto">
          Practical hands-on training, mentorship and coaching empowering youth
          to be self-reliant agents of change
        </p>
      </div>

      <!-- Main Content Grid -->
      <div
        class="grid lg:grid-cols-12 gap-8"
        @mouseenter="pauseSlide"
        @mouseleave="resumeSlide"
      >
        <!-- Left - Service Tabs -->
        <div
          class="lg:col-span-5"
          data-aos="fade-right"
          data-aos-duration="800"
        >
          <div class="space-y-3">
            <div
              v-for="(service, index) in services"
              :key="index"
              @click="setActiveService(index)"
              class="group cursor-pointer"
            >
              <div
                class="relative p-5 rounded-2xl transition-all duration-300"
                :class="
                  activeService === index
                    ? 'bg-logo-color text-white shadow-xl shadow-logo-color/20'
                    : 'bg-white hover:bg-gray-100 text-gray-800'
                "
              >
                <div class="flex items-center gap-4">
                  <!-- Icon -->
                  <div
                    class="w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0 transition-colors"
                    :class="
                      activeService === index
                        ? 'bg-white/20'
                        : 'bg-logo-color/10'
                    "
                  >
                    <i
                      :class="service.icon"
                      class="text-xl transition-colors"
                      :style="{
                        color: activeService === index ? 'white' : '',
                      }"
                    ></i>
                  </div>

                  <!-- Content -->
                  <div class="flex-1 min-w-0">
                    <h3
                      class="font-bold text-lg truncate"
                      :class="
                        activeService === index ? 'text-white' : 'text-gray-900'
                      "
                    >
                      {{ service.title }}
                    </h3>
                    <p
                      class="text-sm truncate"
                      :class="
                        activeService === index
                          ? 'text-white/70'
                          : 'text-gray-500'
                      "
                    >
                      {{ service.features[0] }} & more
                    </p>
                  </div>

                  <!-- Arrow -->
                  <div
                    class="w-10 h-10 rounded-full flex items-center justify-center transition-all"
                    :class="
                      activeService === index
                        ? 'bg-white/20'
                        : 'bg-transparent group-hover:bg-logo-color/10'
                    "
                  >
                    <i
                      class="fas fa-chevron-right text-sm transition-transform"
                      :class="{
                        'translate-x-1': activeService === index,
                        'text-white': activeService === index,
                        'text-gray-400': activeService !== index,
                      }"
                    ></i>
                  </div>
                </div>

                <!-- Active Indicator -->
                <div
                  v-if="activeService === index"
                  class="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-12 bg-white rounded-r-full"
                ></div>
              </div>
            </div>
          </div>

          <!-- Progress Indicators -->
          <div class="flex gap-2 mt-6">
            <div
              v-for="(service, index) in services"
              :key="'progress-' + index"
              class="flex-1 h-1 rounded-full bg-gray-200 overflow-hidden cursor-pointer"
              @click="setActiveService(index)"
            >
              <div
                class="h-full bg-logo-color rounded-full"
                :class="{
                  'animate-progress': activeService === index && !isPaused,
                  'w-full': index < activeService,
                  'w-0': index > activeService,
                }"
                :style="activeService === index ? {} : {}"
              ></div>
            </div>
          </div>

          <!-- View All Link -->
          <div class="mt-8 text-center lg:text-left">
            <router-link
              to="/services"
              class="inline-flex items-center gap-2 text-logo-color font-semibold hover:gap-3 transition-all"
            >
              <span>View All Programs</span>
              <i class="fas fa-arrow-right"></i>
            </router-link>
          </div>
        </div>

        <!-- Right - Active Service Details -->
        <div
          class="lg:col-span-7"
          data-aos="fade-left"
          data-aos-duration="800"
          data-aos-delay="200"
        >
          <div class="relative">
            <!-- Main Image Card -->
            <div class="relative rounded-3xl overflow-hidden shadow-2xl">
              <transition name="slide-fade" mode="out-in">
                <img
                  :key="activeService"
                  :src="require(`@/assets/${services[activeService].imageSrc}`)"
                  :alt="services[activeService].title"
                  class="w-full h-[400px] lg:h-[500px] object-cover"
                />
              </transition>

              <!-- Gradient Overlay -->
              <div
                class="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent"
              ></div>

              <!-- Content Overlay -->
              <div class="absolute bottom-0 left-0 right-0 p-8 text-shadow">
                <!-- Number Badge -->
                <div
                  class="inline-flex items-center gap-2 px-4 py-2 bg-white/20 backdrop-blur-sm rounded-full mb-4"
                >
                  <span class="text-white font-bold">
                    0{{ activeService + 1 }}
                  </span>
                  <span class="text-white/70">/</span>
                  <span class="text-white/70">0{{ services.length }}</span>
                </div>

                <!-- Title -->
                <h3 class="text-2xl lg:text-3xl font-bold text-white mb-3">
                  {{ services[activeService].title }}
                </h3>

                <!-- Description -->
                <p class="text-white/90 mb-6 max-w-xl leading-relaxed">
                  {{ services[activeService].description }}
                </p>

                <!-- Features -->
                <div class="flex flex-wrap gap-2 mb-6">
                  <span
                    v-for="(feature, idx) in services[activeService].features"
                    :key="idx"
                    class="px-4 py-2 bg-white/20 backdrop-blur-sm rounded-full text-white text-sm font-medium"
                  >
                    {{ feature }}
                  </span>
                </div>

                <!-- CTA Button -->
                <router-link
                  :to="services[activeService].link"
                  class="inline-flex items-center gap-3 px-6 py-3 bg-white text-logo-color font-semibold rounded-full hover:shadow-lg hover:gap-4 transition-all duration-300"
                >
                  <span>Learn More</span>
                  <i class="fas fa-arrow-right"></i>
                </router-link>
              </div>
            </div>

            <!-- Decorative Elements -->
            <div
              class="absolute -top-4 -right-4 w-24 h-24 bg-logo-color/20 rounded-2xl -z-10 hidden lg:block"
            ></div>
            <div
              class="absolute -bottom-4 -left-4 w-32 h-32 bg-logo-color/10 rounded-full -z-10 hidden lg:block"
            ></div>

            <!-- Floating Stats Card -->
            <div
              class="absolute -bottom-6 -right-6 bg-white rounded-2xl shadow-xl p-5 z-10 hidden lg:block"
            >
              <div class="flex items-center gap-4">
                <div
                  class="w-12 h-12 bg-logo-color/10 rounded-xl flex items-center justify-center"
                >
                  <i class="fas fa-users text-logo-color text-xl"></i>
                </div>
                <div>
                  <div class="text-2xl font-bold text-logo-color">500+</div>
                  <div class="text-sm text-gray-500">Youth Impacted</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped>
/* Smooth transitions for service changes */
.fade-enter-active,
.fade-leave-active {
  transition: all 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  transform: translateY(10px);
}

/* Progress bar animation */
@keyframes progressFill {
  from {
    width: 0%;
  }
  to {
    width: 100%;
  }
}

.animate-progress {
  animation: progressFill 2.5s linear forwards;
}

/* Slide fade transition for image */
.slide-fade-enter-active {
  transition: all 0.4s ease-out;
}

.slide-fade-leave-active {
  transition: all 0.3s ease-in;
}

.slide-fade-enter-from {
  opacity: 0;
  transform: scale(1.05);
}

.slide-fade-leave-to {
  opacity: 0;
  transform: scale(0.95);
}

/* Text shadow for better readability */
.text-shadow {
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
}

.text-shadow h3 {
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.6);
}
</style>
